import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, type NavigateOptions, type To } from 'react-router-dom';
import { askBeforeLeaving } from '../lib/leavePage';

/** A history entry, as far as the guard needs one: its address and its state. */
type HistoryPlace = { href: string; state: unknown };

function withoutHash(href: string): string {
  const cut = href.indexOf('#');
  return cut === -1 ? href : href.slice(0, cut);
}

/** Ask the newest unsaved-changes question, if there is one; true to go on. */
function askToLeave(guards: ReadonlyMap<symbol, string>): boolean {
  const message = Array.from(guards.values()).at(-1);
  return !message || window.confirm(message);
}

type NavigationGuardContextValue = {
  register: (id: symbol, message: string) => void;
  unregister: (id: symbol) => void;
  confirmNavigation: () => boolean;
};

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(null);

export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  const guards = useRef(new Map<symbol, string>());
  const [revision, setRevision] = useState(0);
  const register = useCallback((id: symbol, message: string) => {
    guards.current.set(id, message);
    setRevision((current) => current + 1);
  }, []);
  const unregister = useCallback((id: symbol) => {
    if (!guards.current.delete(id)) return;
    setRevision((current) => current + 1);
  }, []);
  const confirmNavigation = useCallback(() => askToLeave(guards.current), []);

  // The history entry the app shows now, kept on every navigation the router
  // commits, so a refused Back or Forward can be put back.
  const location = useLocation();
  const shown = useRef<HistoryPlace>({ href: '', state: null });
  useLayoutEffect(() => {
    shown.current = { href: window.location.href, state: window.history.state };
  }, [location]);

  // Back and Forward. Subscribed once, as this provider mounts, which is
  // before the router subscribes (a child's layout effects run before its
  // parent's, and BrowserRouter subscribes in its own), so this listener runs
  // first. It has to: the router renders the page a step leads to inside its
  // own listener, which takes down the page holding the unsaved changes, and
  // the question with it, before a listener after it is called. A refused step
  // never reaches the router. Nothing here may make it subscribe again (no
  // dependencies): subscribing again would put it after the router.
  useLayoutEffect(() => {
    const historyNavigation = (event: PopStateEvent) => {
      const from = shown.current;
      // A step within the page, to or from a fragment of it, takes nothing
      // down, as a link to a fragment does not.
      if (withoutHash(window.location.href) === withoutHash(from.href)) return;
      if (askToLeave(guards.current)) return;
      event.stopImmediatePropagation();
      // The entry the app shows goes back on top. Going back by the distance
      // the step went would keep the entries after it, but the distance is
      // known only from the router's index on each entry, and that counts
      // positions only while every entry between was written by the router
      // from an entry it wrote: a link to a fragment, or another script's
      // history entry, shifts it. A return measured wrong lands on another
      // entry, and asks again, or goes nowhere. `pushState` cannot miss: the
      // address is the app's again at once, the router (which never heard of
      // the step) is unchanged, and nothing is left to wait for. The cost is
      // the cost of any new entry: those after the one the step reached are
      // dropped.
      window.history.pushState(from.state, '', from.href);
    };
    window.addEventListener('popstate', historyNavigation);
    return () => window.removeEventListener('popstate', historyNavigation);
  }, []);

  useEffect(() => {
    if (guards.current.size === 0) return undefined;
    const anchorNavigation = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey
        || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(anchor instanceof HTMLAnchorElement) || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const destination = new URL(anchor.href, window.location.href);
      // A link within this page, to a fragment of it, leaves nothing behind.
      const current = new URL(window.location.href);
      destination.hash = '';
      current.hash = '';
      if (destination.href === current.href || confirmNavigation()) return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('beforeunload', askBeforeLeaving);
    document.addEventListener('click', anchorNavigation, true);
    return () => {
      window.removeEventListener('beforeunload', askBeforeLeaving);
      document.removeEventListener('click', anchorNavigation, true);
    };
  }, [confirmNavigation, revision]);

  const value = useMemo(() => ({ register, unregister, confirmNavigation }), [confirmNavigation, register, unregister]);
  return <NavigationGuardContext.Provider value={value}>{children}</NavigationGuardContext.Provider>;
}

function useNavigationGuardContext() {
  const context = useContext(NavigationGuardContext);
  if (!context) throw new Error('Navigation guards require NavigationGuardProvider.');
  return context;
}

export function useUnsavedNavigationGuard(active: boolean, message: string) {
  const { register, unregister } = useNavigationGuardContext();
  const id = useRef(Symbol('navigation-guard'));
  useEffect(() => {
    const guardId = id.current;
    if (active) register(guardId, message);
    else unregister(guardId);
    return () => unregister(guardId);
  }, [active, message, register, unregister]);
}

export function useNavigationConfirmation() {
  return useNavigationGuardContext().confirmNavigation;
}

export function useGuardedNavigate() {
  const navigate = useNavigate();
  const confirmNavigation = useNavigationConfirmation();
  return useCallback((to: To, options?: NavigateOptions) => {
    if (confirmNavigation()) navigate(to, options);
  }, [confirmNavigation, navigate]);
}
